import { useState, useEffect, useCallback } from "react";
import { Bell, BellRing, UtensilsCrossed, Bed, CheckCheck, ShieldAlert, User, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { ReservationDetailModal } from "./ReservationDetailModal";
import { formatDistanceToNow, format } from "date-fns";
import { es } from "date-fns/locale";
import { usePushNotifications } from "@/hooks/usePushNotifications";

type Notification = {
  id: string;
  reservation_id: string | null;
  restaurant_reservation_id: string | null;
  notification_type: string;
  title: string;
  message: string;
  is_read: boolean;
  read_by: string[];
  created_at: string;
};

type ReservationInfo = {
  id: string;
  status: string | null;
  payment_status: string | null;
  payment_id: string | null;
  deposit_amount: number | null;
  deposit_paid: boolean | null;
  total: number;
  first_name: string;
  last_name: string;
  check_in: string;
  check_out: string;
  guests: number;
  accommodation_id: string | null;
  payment_method: string | null;
};

type AccommodationInfo = {
  id: string;
  name: string;
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", minimumFractionDigits: 0 }).format(value);

export function NotificationBell() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [reservationsMap, setReservationsMap] = useState<Map<string, ReservationInfo>>(new Map());
  const [accommodationsMap, setAccommodationsMap] = useState<Map<string, AccommodationInfo>>(new Map());
  const [isOpen, setIsOpen] = useState(false);
  const [selectedReservationId, setSelectedReservationId] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const { isSupported, isSubscribed, subscribe } = usePushNotifications();

  const fetchNotifications = useCallback(async () => {
    const { data, error } = await supabase
      .from("admin_notifications")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(50);

    if (!error && data) {
      setNotifications(data);

      // Fetch reservation details to enrich notifications
      const resIds = data
        .filter(n => n.reservation_id)
        .map(n => n.reservation_id!)
        .filter((v, i, a) => a.indexOf(v) === i);

      if (resIds.length > 0) {
        const { data: resData } = await supabase
          .from("reservations")
          .select("id, status, payment_status, payment_id, deposit_amount, deposit_paid, total, first_name, last_name, check_in, check_out, guests, accommodation_id, payment_method")
          .in("id", resIds);

        if (resData) {
          const map = new Map<string, ReservationInfo>();
          resData.forEach(r => map.set(r.id, r));
          setReservationsMap(map);

          // Fetch accommodation names
          const accIds = resData
            .filter(r => r.accommodation_id)
            .map(r => r.accommodation_id!)
            .filter((v, i, a) => a.indexOf(v) === i);

          if (accIds.length > 0) {
            const { data: accData } = await supabase
              .from("accommodations_public")
              .select("id, name")
              .in("id", accIds);
            if (accData) {
              const accMap = new Map<string, AccommodationInfo>();
              accData.forEach(a => accMap.set(a.id, a));
              setAccommodationsMap(accMap);
            }
          }
        }
      }
    }
  }, []);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (data.user) {
        setUserId(data.user.id);
        if (isSupported && !isSubscribed) {
          subscribe();
        }
      }
    });

    fetchNotifications();

    const channel = supabase
      .channel("admin-notifications")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "admin_notifications" },
        (payload) => {
          const newNotification = payload.new as Notification;
          setNotifications((prev) => [newNotification, ...prev].slice(0, 50));
          // Re-fetch to get reservation details
          fetchNotifications();
          toast.info(newNotification.title, {
            description: newNotification.message,
            action: newNotification.reservation_id
              ? { label: "Ver", onClick: () => setSelectedReservationId(newNotification.reservation_id) }
              : undefined,
            duration: 8000,
          });
        }
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "admin_notifications" },
        (payload) => {
          const updated = payload.new as Notification;
          setNotifications((prev) => prev.map((n) => (n.id === updated.id ? updated : n)));
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [fetchNotifications, isSupported, isSubscribed, subscribe]);

  // Classify notifications
  const classifyNotification = (n: Notification): "web" | "abandoned" | "manual" | "restaurant" | "other" => {
    if (n.notification_type === "new_restaurant_reservation" || n.restaurant_reservation_id) return "restaurant";
    if (!n.reservation_id) return "other";

    const res = reservationsMap.get(n.reservation_id);
    if (!res) return "other";

    // Abandoned = cancelled/rejected/expired with no deposit paid
    if (res.status === "cancelled" || res.payment_status === "rejected" || res.payment_status === "expired") {
      return "abandoned";
    }

    // Web = has payment_id (went through Mercado Pago online)
    if (res.payment_id) return "web";

    // Manual = created by staff in calendar (no payment_id)
    if (res.status === "confirmed" || res.status === "pending") return "manual";

    return "other";
  };

  const unreadCount = notifications.filter(
    (n) => !n.read_by.includes(userId || "")
  ).length;

  const markAsRead = async (notificationId: string) => {
    if (!userId) return;
    const notification = notifications.find((n) => n.id === notificationId);
    if (!notification || notification.read_by.includes(userId)) return;

    const newReadBy = [...notification.read_by, userId];

    // Optimistic update first
    setNotifications((prev) =>
      prev.map((n) => (n.id === notificationId ? { ...n, read_by: newReadBy } : n))
    );

    const { error } = await supabase
      .from("admin_notifications")
      .update({ read_by: newReadBy })
      .eq("id", notificationId);

    if (error) {
      // Revert on error
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, read_by: notification.read_by } : n))
      );
      console.error("Error marking notification as read:", error);
    }
  };

  const markAllAsRead = async () => {
    if (!userId) return;
    const unread = notifications.filter((n) => !n.read_by.includes(userId));
    if (unread.length === 0) return;

    // Optimistic update
    setNotifications((prev) =>
      prev.map((n) => ({
        ...n,
        read_by: n.read_by.includes(userId) ? n.read_by : [...n.read_by, userId],
      }))
    );

    // Batch update all at once
    const updatePromises = unread.map(n => 
      supabase.from("admin_notifications")
        .update({ read_by: [...n.read_by, userId] })
        .eq("id", n.id)
    );
    
    const results = await Promise.all(updatePromises);
    const hasErrors = results.some(r => r.error);
    if (hasErrors) {
      console.error("Some notifications failed to mark as read");
      fetchNotifications(); // Re-fetch to get correct state
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    markAsRead(notification.id);
    if (notification.reservation_id) {
      setSelectedReservationId(notification.reservation_id);
    }
  };

  const getNotificationContent = (n: Notification) => {
    const type = classifyNotification(n);
    const res = n.reservation_id ? reservationsMap.get(n.reservation_id) : null;
    const acc = res?.accommodation_id ? accommodationsMap.get(res.accommodation_id) : null;

    if (type === "abandoned") {
      return {
        icon: <ShieldAlert className="h-5 w-5" />,
        iconClass: "bg-muted text-muted-foreground",
        badge: <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-muted text-muted-foreground border-muted-foreground/20">Abandonada</Badge>,
        title: `${res?.first_name} ${res?.last_name}`,
        subtitle: `Carrito abandonado · ${acc?.name || ""}`,
        details: res ? `${format(new Date(res.check_in + "T12:00:00"), "d MMM", { locale: es })} - ${format(new Date(res.check_out + "T12:00:00"), "d MMM", { locale: es })} · ${res.guests} huéspedes` : n.message,
        amount: null,
        dimmed: true,
      };
    }

    if (type === "manual") {
      return {
        icon: <User className="h-5 w-5" />,
        iconClass: "bg-secondary text-secondary-foreground",
        badge: <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-secondary/50 text-secondary-foreground border-secondary/30">Manual</Badge>,
        title: `${res?.first_name} ${res?.last_name}`,
        subtitle: acc?.name || "Reserva manual",
        details: res ? `${format(new Date(res.check_in + "T12:00:00"), "d MMM", { locale: es })} - ${format(new Date(res.check_out + "T12:00:00"), "d MMM", { locale: es })} · ${res.guests} huéspedes` : n.message,
        amount: res ? formatCurrency(res.total) : null,
        dimmed: false,
      };
    }

    if (type === "web") {
      const depositText = res?.deposit_paid && res.deposit_amount
        ? `Anticipo: ${formatCurrency(res.deposit_amount)}`
        : null;
      return {
        icon: <Bed className="h-5 w-5" />,
        iconClass: "bg-primary/10 text-primary",
        badge: <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-blue-50 text-blue-700 border-blue-200">Web</Badge>,
        title: `${res?.first_name} ${res?.last_name}`,
        subtitle: acc?.name || "",
        details: res ? `${format(new Date(res.check_in + "T12:00:00"), "d MMM", { locale: es })} - ${format(new Date(res.check_out + "T12:00:00"), "d MMM", { locale: es })} · ${res.guests} huéspedes` : n.message,
        amount: res ? formatCurrency(res.total) : null,
        depositText,
        dimmed: false,
      };
    }

    if (type === "restaurant") {
      return {
        icon: <UtensilsCrossed className="h-5 w-5" />,
        iconClass: "bg-accent text-accent-foreground",
        badge: <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-accent/50 text-accent-foreground border-accent/30">Restaurante</Badge>,
        title: n.title,
        subtitle: "",
        details: n.message,
        amount: null,
        dimmed: false,
      };
    }

    return {
      icon: <BellRing className="h-5 w-5" />,
      iconClass: "bg-muted text-muted-foreground",
      badge: null,
      title: n.title,
      subtitle: "",
      details: n.message,
      amount: null,
      dimmed: false,
    };
  };

  // Separate active vs abandoned
  const activeNotifications = notifications.filter(n => classifyNotification(n) !== "abandoned");
  const abandonedNotifications = notifications.filter(n => classifyNotification(n) === "abandoned");
  const activeUnreadCount = activeNotifications.filter(n => !n.read_by.includes(userId || "")).length;

  return (
    <>
      <Button variant="ghost" size="icon" className="relative" onClick={() => setIsOpen(true)}>
        <Bell className="h-5 w-5" />
        {activeUnreadCount > 0 && (
          <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-destructive text-destructive-foreground text-xs flex items-center justify-center font-medium animate-pulse">
            {activeUnreadCount > 9 ? "9+" : activeUnreadCount}
          </span>
        )}
      </Button>

      <Dialog open={isOpen} onOpenChange={(open) => {
        setIsOpen(open);
        if (open) fetchNotifications();
      }}>
        <DialogContent className="max-w-lg w-full max-h-[85vh] p-0 gap-0 overflow-hidden">
          <DialogHeader className="px-5 py-4 border-b bg-muted/30">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-lg font-bold flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Notificaciones
                {activeUnreadCount > 0 && (
                  <Badge className="bg-destructive text-destructive-foreground text-xs">
                    {activeUnreadCount} nuevas
                  </Badge>
                )}
              </DialogTitle>
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs gap-1.5 text-primary"
                  onClick={markAllAsRead}
                >
                  <CheckCheck className="h-3.5 w-3.5" />
                  Marcar todo como leído
                </Button>
              )}
            </div>
          </DialogHeader>

          <ScrollArea className="flex-1" style={{ maxHeight: "calc(85vh - 70px)" }}>
            {activeNotifications.length === 0 && abandonedNotifications.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">
                No hay notificaciones
              </div>
            ) : (
              <div>
                {/* Active notifications */}
                {activeNotifications.length > 0 && (
                  <div className="divide-y">
                    {activeNotifications.map((notification) => {
                      const isUnread = !notification.read_by.includes(userId || "");
                      const content = getNotificationContent(notification);

                      return (
                        <button
                          key={notification.id}
                          onClick={() => handleNotificationClick(notification)}
                          className={cn(
                            "w-full text-left px-5 py-3.5 hover:bg-muted/50 transition-colors flex items-start gap-3",
                            isUnread && "bg-primary/5"
                          )}
                        >
                          <div className={cn(
                            "mt-0.5 h-10 w-10 rounded-full flex items-center justify-center shrink-0",
                            content.iconClass
                          )}>
                            {content.icon}
                          </div>

                          <div className="flex-1 min-w-0 space-y-0.5">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={cn(
                                "text-sm font-semibold leading-snug",
                                isUnread ? "text-foreground" : "text-foreground/80"
                              )}>
                                {content.title}
                              </span>
                              {content.badge}
                            </div>
                            {content.subtitle && (
                              <p className="text-xs text-primary font-medium">{content.subtitle}</p>
                            )}
                            <p className="text-xs text-muted-foreground leading-snug">{content.details}</p>
                            <div className="flex items-center gap-3">
                              {content.amount && (
                                <span className="text-xs font-semibold text-foreground">Total: {content.amount}</span>
                              )}
                              {(content as any).depositText && (
                                <span className="text-xs text-green-600 font-medium">{(content as any).depositText}</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {(notification.reservation_id || notification.restaurant_reservation_id) && (
                                <span className="text-xs text-primary font-medium hover:underline">Ver reserva</span>
                              )}
                              <span className="text-[11px] text-muted-foreground/50">
                                {formatDistanceToNow(new Date(notification.created_at), {
                                  addSuffix: true,
                                  locale: es,
                                })}
                              </span>
                            </div>
                          </div>

                          {isUnread && (
                            <span className="mt-2 h-2.5 w-2.5 rounded-full bg-primary shrink-0" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Abandoned section */}
                {abandonedNotifications.length > 0 && (
                  <div>
                    <div className="px-5 py-2 bg-muted/50 border-y">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                        <ShieldAlert className="h-3.5 w-3.5" />
                        Carritos abandonados ({abandonedNotifications.length})
                      </p>
                    </div>
                    <div className="divide-y opacity-60">
                      {abandonedNotifications.map((notification) => {
                        const content = getNotificationContent(notification);
                        const res = notification.reservation_id ? reservationsMap.get(notification.reservation_id) : null;

                        return (
                          <div
                            key={notification.id}
                            className="w-full text-left px-5 py-2.5 flex items-center gap-3"
                          >
                            <div className={cn(
                              "h-8 w-8 rounded-full flex items-center justify-center shrink-0",
                              content.iconClass
                            )}>
                              {content.icon}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-muted-foreground line-through">
                                {res ? `${res.first_name} ${res.last_name}` : notification.title}
                              </p>
                              <p className="text-[11px] text-muted-foreground/50">
                                {res?.payment_status === "rejected" ? "Pago rechazado" : res?.payment_status === "expired" ? "Pago expirado" : "Cancelada"}
                              </p>
                            </div>
                            <span className="text-[11px] text-muted-foreground/50">
                              {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true, locale: es })}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      <ReservationDetailModal
        reservationId={selectedReservationId}
        onClose={() => setSelectedReservationId(null)}
      />
    </>
  );
}
